export const LIFETIME_PRODUCT_ID = 'carryonpan_lifetime' as const;

export type CommerceChannel = 'web' | 'apps_in_toss' | 'ios' | 'android';
export type EntitlementState =
  | 'unavailable'
  | 'ready'
  | 'pending'
  | 'paid_ungranted'
  | 'granted'
  | 'restored'
  | 'refunded'
  | 'chargeback'
  | 'revoked';

export interface Rights {
  maxTrips: 2 | 30;
  maxBagsPerTrip: 4 | 10;
  maxItemsPerTrip: 120 | 200;
  maxBaseLists: 1 | 10;
  customPresets: boolean;
  fullHistory: boolean;
}

export const FREE_RIGHTS: Rights = { maxTrips: 2, maxBagsPerTrip: 4, maxItemsPerTrip: 120, maxBaseLists: 1, customPresets: false, fullHistory: false };
export const LIFETIME_RIGHTS: Rights = { maxTrips: 30, maxBagsPerTrip: 10, maxItemsPerTrip: 200, maxBaseLists: 10, customPresets: true, fullHistory: true };

export function hasVerifiedLifetime(state: EntitlementState) {
  return state === 'granted' || state === 'restored';
}

export function rightsFor(state: EntitlementState): Rights {
  return hasVerifiedLifetime(state) ? LIFETIME_RIGHTS : FREE_RIGHTS;
}

export type EntitlementEventType =
  | 'provider_available'
  | 'order_created'
  | 'provider_paid'
  | 'grant_confirmed'
  | 'restore_confirmed'
  | 'refund_confirmed'
  | 'chargeback_confirmed'
  | 'entitlement_revoked'
  | 'provider_unavailable'
  | 'reconciliation_matched'
  | 'reconciliation_mismatch';

export interface EntitlementEvent {
  id: string;
  type: EntitlementEventType;
  occurredAt: string;
  channel: CommerceChannel;
  providerOrderRef?: string;
  reasonCode?: string;
}

export interface EntitlementLedger {
  productId: typeof LIFETIME_PRODUCT_ID;
  subjectRef: string;
  state: EntitlementState;
  sourceChannel: CommerceChannel | null;
  providerOrderRef: string | null;
  appliedEventIds: string[];
  updatedAt: string | null;
  lastReconciledAt: string | null;
}

export function createEntitlementLedger(subjectRef: string): EntitlementLedger {
  return {
    productId: LIFETIME_PRODUCT_ID,
    subjectRef,
    state: 'unavailable',
    sourceChannel: null,
    providerOrderRef: null,
    appliedEventIds: [],
    updatedAt: null,
    lastReconciledAt: null,
  };
}

export function transitionEntitlement(current: EntitlementState, event: EntitlementEventType): EntitlementState {
  if (event === 'refund_confirmed') return 'refunded';
  if (event === 'chargeback_confirmed') return 'chargeback';
  if (event === 'entitlement_revoked') return 'revoked';
  if (event === 'provider_unavailable') return hasVerifiedLifetime(current) ? current : 'unavailable';
  if (event === 'provider_available' && ['unavailable', 'refunded', 'chargeback', 'revoked'].includes(current)) return 'ready';
  if (event === 'order_created' && current === 'ready') return 'pending';
  if (event === 'provider_paid' && current === 'pending') return 'paid_ungranted';
  if (event === 'grant_confirmed' && (current === 'pending' || current === 'paid_ungranted')) return 'granted';
  if (event === 'restore_confirmed') return 'restored';
  return current;
}

/**
 * Projects an append-only, provider-verified event stream. Duplicate webhook or
 * store history events are idempotent and client order callbacks never grant.
 */
export function applyEntitlementEvent(ledger: EntitlementLedger, event: EntitlementEvent): EntitlementLedger {
  if (ledger.appliedEventIds.includes(event.id)) return ledger;
  const state = transitionEntitlement(ledger.state, event.type);
  return {
    ...ledger,
    state,
    sourceChannel: state === ledger.state ? ledger.sourceChannel : event.channel,
    providerOrderRef: event.providerOrderRef ?? ledger.providerOrderRef,
    appliedEventIds: [...ledger.appliedEventIds, event.id],
    updatedAt: event.occurredAt,
    lastReconciledAt: event.type.startsWith('reconciliation_') ? event.occurredAt : ledger.lastReconciledAt,
  };
}

export interface CommerceRuntimePolicy {
  providerEnabled: boolean;
  productObserved: boolean;
  providerHealthy: boolean;
  killSwitch: boolean;
}

export type CommerceAvailabilityReason = 'ready' | 'provider_disabled' | 'product_unobserved' | 'provider_outage' | 'kill_switch';

export function evaluateCommerceAvailability(policy: CommerceRuntimePolicy): { available: boolean; reason: CommerceAvailabilityReason } {
  if (policy.killSwitch) return { available: false, reason: 'kill_switch' };
  if (!policy.providerEnabled) return { available: false, reason: 'provider_disabled' };
  if (!policy.productObserved) return { available: false, reason: 'product_unobserved' };
  if (!policy.providerHealthy) return { available: false, reason: 'provider_outage' };
  return { available: true, reason: 'ready' };
}
